import { Chip, Collapse, Divider, Stack, Typography } from '@mui/material';
import { VyIcon } from '@weft/icons';
import { TRIAL_DURATION_TYPES, type Plan, type PlanTrial, type SyncStatus } from '@weft/types';
import type { Currency, PlanBillingData, Product } from '@weft/types';
import { isNotNullOrUndefined, raiseUnreachable, type Optional } from '@weft/utils';
import { useToggle } from 'apps/app/src/hooks/use-toggle';
import { formatDate, monthsToDays, weeksToDays } from 'apps/app/src/utils/date';
import React from 'react';
import { Link } from 'react-router-dom';
import { Price } from '../../../components/design/Price';
import { StatusInfoCard } from '../../../components/design/status-info-card/StatusInfoCard';
import { getPlanDurationLabel } from '../../plans/CreatePlanPage/create-plan-page-components/PlanDurationAutoComplete';
import { getBillingIntervalValues } from '../../plans/CreatePlanPage/create-plan-page-components/PlanSettings';
import { ContractPageAdvancedDetails } from './ContractPageAdvancedDetails';
import { ContractPageBillingSchedule } from './ContractPageBillingSchedule/ContractPageBillingSchedule';

type ContractPageDetailsProps = {
  billingData: PlanBillingData;
  revenue: number;
  contractStartDate: Date;
  customerId: string;
  customerName: string;
  currency: Currency;
  products: Product[];
  contractId: string;
  contractSyncStatus: SyncStatus;
  contractSyncStatusUpdatedAt: Optional<Date>;
  plan: Plan;
};

export const ContractPageDetails: React.FC<ContractPageDetailsProps> = ({ plan, billingData, revenue, contractStartDate, customerId, customerName, currency, products, contractId, contractSyncStatus, contractSyncStatusUpdatedAt }) => {
  const [isAdvancedCollapsed, toggleAdvancedCollapse] = useToggle(true);
  const [isBillingScheduleCollapsed, toggleBillingScheduleCollapse] = useToggle(true);

  return (
    <Stack
      className="group border-line rounded-md border bg-white p-6 shadow-[0px_10px_24px_0px_rgba(0,0,0,0.03)]"
      divider={<Divider/>}
      rowGap={2}
    >
      <div className="mb-4">
        <div className="flex flex-col gap-y-1">
          <Typography
            variant="body1"
            fontWeight={400}
            color='rgba(27,61,85,0.60)'
          >
            Revenue
          </Typography>
          <div className='flex gap-2'>
            <Price
              className="text-[34px] font-light	"
              currency={currency}
            >
              {revenue}
            </Price>
            { isNotNullOrUndefined(plan.trial) && (
              <Chip
                label={`${formatTrialDays(plan.trial)} Days Trial`}
                size='small'
                sx={{
                  backgroundColor: '#F7E9FE',
                  color: '#C350FF',
                  alignSelf: 'center',
                }}
              />
            )}
          </div>
        </div>

        <div className="relative flex flex-wrap gap-x-4 pt-8 before:w-full">
          <StatusInfoCard
            className="flex flex-1"
            title="Billing Interval"
          >
            <div className="flex items-end">
              <p
                className="text-lg font-medium"
              >
                {getBillingIntervalValues().find(({ value }) => value === billingData.billingInterval)?.label}
              </p>
            </div>
          </StatusInfoCard>
          <StatusInfoCard
            className="flex flex-1"
            title="Start Date"
          >
            <div className="flex items-end">
              <p className="text-lg font-medium">{formatDate(contractStartDate)}</p>
            </div>
          </StatusInfoCard>
          <StatusInfoCard
            className="flex flex-1"
            title="Duration"
          >
            <div className="flex items-end">
              <p className="text-lg font-medium">{getPlanDurationLabel(billingData.duration)}</p>
            </div>
          </StatusInfoCard>
          <StatusInfoCard
            className="flex flex-1"
            title="Customer"
          >
            <div className="flex items-end truncate">
              <Link
                className="truncate text-lg font-medium"
                to={`/customers/${customerId}`}
              >
                {customerName}
              </Link>
            </div>
          </StatusInfoCard>
        </div>
      </div>

      <button onClick={() => toggleBillingScheduleCollapse()}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>
            Billing Schedule
          </Typography>

          <VyIcon
            icon="SmallArrow"
            style={{
              width: '32px',
              height: '32px',
              transform: isBillingScheduleCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s',
            }}
          />
        </Stack>

        <Collapse in={!isBillingScheduleCollapsed}>
          <ContractPageBillingSchedule
            contractId={contractId}
            onLoadData={() => {
              if (isBillingScheduleCollapsed) {
                toggleBillingScheduleCollapse();
              }
            }}
            products={products}
            currency={currency}
          />
        </Collapse>
      </button>

      <button onClick={() => toggleAdvancedCollapse()}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction='row'
            columnGap={1}
          >
            <VyIcon
              icon='Settings'
            />
            <Typography>
              Advanced
            </Typography>
          </Stack>
          <VyIcon
            icon="SmallArrow"
            style={{
              width: '32px',
              height: '32px',
              transform: isAdvancedCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s',
            }}
          />
        </Stack>

        <Collapse in={!isAdvancedCollapsed}>
          <ContractPageAdvancedDetails
            products={products.filter(product => isNotNullOrUndefined(product.erpMetadata))}
            contractId={contractId}
            contractSyncStatus={contractSyncStatus}
            contractSyncStatusUpdatedAt={contractSyncStatusUpdatedAt}
          />
        </Collapse>
      </button>
    </Stack>
  );
};

function formatTrialDays(trial: PlanTrial) {
  const { duration, type } = trial;

  switch (type) {
    case TRIAL_DURATION_TYPES.weeks:
      return weeksToDays(duration);
    case TRIAL_DURATION_TYPES.months:
      return monthsToDays(duration);
    default:
      return raiseUnreachable(type);
  }
}